import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BackButton from '../components/BackButton';
import {
  Radio, Calendar, History, Plus, Crown, Clock, Users, Euro,
  Loader2, BarChart3, LayoutGrid, LayoutDashboard, Phone, Trash2, Pencil, X as XIcon,
  Video, Mic, Copy, Ban, Wallet, UserX, Headphones, Gift, Bookmark,
  ArrowDownToLine, CheckCircle2, Hourglass, TrendingUp, Banknote, ReceiptText,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import {
  dashboardOverview, dashboardFuture, dashboardHistory,
  dashboardOneToOne, dashboardCalendar, dashboardStats, creatorSummary,
} from '../api/livesDashboard';
import api from '../api/client';
import { fetchUserPosts } from '../api/posts';
import { deleteLive, startLive, cancelLive, duplicateLive, getLive } from '../api/lives';
import { LiveScheduleCountdown } from '../components/LiveScheduleCountdown';
import { fmtCredits, creditsToEur } from '../lib/currency';
import CreateLiveDialog from '../components/CreateLiveDialog';
import GoLiveNowDialog from '../components/GoLiveNowDialog';
import ReadOnlyNotice from '../components/ReadOnlyNotice';
import EarnWithLivesHero from '../components/EarnWithLivesHero';
import OneToOneVipRequiredDialog from '../components/OneToOneVipRequiredDialog';
import useAppConfig from '../hooks/useAppConfig';
import { canWrite } from '../lib/canWrite';
// Phase 2D · Fase 7 — moved from WalletPage to Live dashboard where it
// belongs semantically (One-to-One is a live-session feature, not a
// wallet/payout feature).
import CreatorCallSettings, { CreatorCallHistory } from '../components/o2ocall/CreatorCallSettings';
// Phase F9.6 — riordino UI: monitoraggio economico ricollocato qui dal
// Wallet (stessi componenti, stessi endpoint — solo il mount point cambia).
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { requestPayout } from '../api/payments';
import EarningsDashboard from '../components/EarningsDashboard';
import WalletLedger from '../components/WalletLedger';
import RefundRequestCard from '../components/RefundRequestCard';
import PayoutOnboardingCard from '../components/PayoutOnboardingCard';
import BuyerOnboardingCard from '../components/BuyerOnboardingCard';
import { useWalletRole } from '../hooks/useWalletRole';

/**
 * Phase 20 — Live Dashboard (private).
 */

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const fmtMoney = (n) => fmtCredits(n);

const StatTile = ({ icon: Icon, label, value, testId, accent }) => (
  <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-4" data-testid={testId}>
    <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${
      accent ? 'gold-gradient text-black' : 'bg-secondary gold-text'
    }`}>
      <Icon className="h-5 w-5" />
    </span>
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold tabular-nums">{value}</p>
    </div>
  </div>
);

const TabBtn = ({ id, label, icon: Icon, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`relative flex shrink-0 items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-xs font-semibold transition ${
      active ? 'gold-gradient text-black shadow' : 'text-muted-foreground hover:bg-secondary'
    }`}
  >
    <Icon className="h-3.5 w-3.5" />
    {label}
  </button>
);

const Empty = ({ icon: Icon, text, testId }) => (
  <div
    className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border py-16 text-muted-foreground"
    data-testid={testId}
  >
    <Icon className="h-8 w-8 opacity-50" />
    <p className="text-sm">{text}</p>
  </div>
);

const OverviewTab = ({ data, isVip, t, wallet, blocked, creatorSum, posts, calls, stats, navigate, openCalendar, openCalls }) => {
  // Pannello di controllo — role-aware overview. READS existing data only.
  const src = (k) => creatorSum?.earnings_by_source?.[k] || null;
  const sum = (keys, field) => keys.reduce((a, k) => a + (src(k)?.[field] || 0), 0);
  const racconti = (posts || []).filter((p) => p.media_type === 'audio' || p.media_type === 'text');
  const audioN = racconti.filter((p) => p.media_type === 'audio').length;
  const textN = racconti.filter((p) => p.media_type === 'text').length;
  const premiumN = racconti.filter((p) => p.is_paid).length;
  const completedCalls = (calls || []).filter((c) => c.state === 'COMPLETED');
  const missedCalls = (calls || []).filter((c) => ['REJECTED', 'TIMEOUT', 'CANCELLED'].includes(c.state));
  const callMinutes = completedCalls.reduce((a, c) => a + (c.billed_minutes || Math.round((c.duration_seconds || 0) / 60)), 0);
  const callEarn = completedCalls.reduce((a, c) => a + (c.creator_net_credits || 0), 0);
  const avgCall = completedCalls.length ? (callMinutes / completedCalls.length).toFixed(1) : '0';
  const p = (k) => t(`dashboard.panel.${k}`);

  const Kpi = ({ label, value, testId }) => (
    <div className="rounded-lg bg-secondary/60 px-3 py-2.5" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-base font-semibold tabular-nums">{value}</p>
    </div>
  );
  const PanelCard = ({ title, icon: Icon, children, testId, actionLabel, onAction, actionTestId }) => (
    <section className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider gold-text">
          <Icon className="h-4 w-4" /> {title}
        </h3>
        {actionLabel && (
          <button
            type="button"
            onClick={onAction}
            className="rounded-full border border-border px-3 py-1 text-[11px] font-semibold text-muted-foreground hover:bg-secondary"
            data-testid={actionTestId}
          >
            {actionLabel}
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">{children}</div>
    </section>
  );

  return (
    <div className="grid gap-3 md:grid-cols-2" data-testid="dashboard-overview">
      <PanelCard title={p('wallet_title')} icon={Wallet} testId="panel-wallet" actionLabel={p('open_wallet')} onAction={() => navigate('/wallet')} actionTestId="panel-open-wallet">
        <Kpi label={p('balance')} value={fmtMoney(wallet?.available_balance ?? 0)} testId="panel-balance" />
        <Kpi label={p('purchased')} value={fmtMoney(wallet?.total_purchased ?? 0)} testId="panel-purchased" />
        <Kpi label={p('spent')} value={fmtMoney(wallet?.total_spent ?? 0)} testId="panel-spent" />
      </PanelCard>

      <PanelCard title={p('account_title')} icon={UserX} testId="panel-account" actionLabel={p('manage')} onAction={() => navigate('/settings')} actionTestId="panel-manage-blocked">
        <Kpi label={p('blocked')} value={(blocked || []).length} testId="panel-blocked" />
        <Kpi label={t('dashboard.overview.one_to_one_requests')} value={data.one_to_one_requests} testId="panel-o2o-req" />
      </PanelCard>

      <PanelCard title={p('lives_title')} icon={Radio} testId="panel-lives" actionLabel={p('open_calendar')} onAction={openCalendar} actionTestId="panel-open-calendar">
        <Kpi label={p('lives_done')} value={data.completed_count} testId="panel-lives-done" />
        <Kpi label={p('lives_planned')} value={data.future_count} testId="panel-lives-planned" />
        <Kpi label={p('bookings')} value={data.total_bookings} testId="panel-bookings" />
      </PanelCard>

      {isVip && stats && (
        <PanelCard title={p('earnings_title')} icon={Euro} testId="panel-earnings">
          <Kpi label={p('earn_total')} value={fmtMoney(stats.revenue_total)} testId="panel-earn-total" />
          <Kpi label={p('earn_month')} value={fmtMoney(stats.revenue_this_month)} testId="panel-earn-month" />
          <Kpi label={p('earn_racconti')} value={fmtMoney(sum(['post'], 'net'))} testId="panel-earn-racconti" />
          <Kpi label={p('earn_lives')} value={fmtMoney(sum(['live', 'live_ticket'], 'net'))} testId="panel-earn-lives" />
          <Kpi label={p('earn_calls')} value={fmtMoney(sum(['o2o_call'], 'net'))} testId="panel-earn-calls" />
          <Kpi label={p('earn_tips')} value={fmtMoney(sum(['tip'], 'net'))} testId="panel-earn-tips" />
        </PanelCard>
      )}

      {isVip && creatorSum && (
        <PanelCard title={p('sales_title')} icon={Gift} testId="panel-sales">
          <Kpi label={p('sales_posts')} value={sum(['post'], 'count')} testId="panel-sales-posts" />
          <Kpi label={p('sales_tickets')} value={sum(['live', 'live_ticket'], 'count')} testId="panel-sales-tickets" />
          <Kpi label={p('sales_calls')} value={sum(['o2o_call'], 'count')} testId="panel-sales-calls" />
          <Kpi label={p('sales_tips')} value={sum(['tip'], 'count')} testId="panel-sales-tips" />
        </PanelCard>
      )}

      {isVip && (
        <PanelCard title={p('racconti_title')} icon={Headphones} testId="panel-racconti">
          <Kpi label={p('racconti_audio')} value={audioN} testId="panel-racconti-audio" />
          <Kpi label={p('racconti_text')} value={textN} testId="panel-racconti-text" />
          <Kpi label={p('racconti_premium')} value={premiumN} testId="panel-racconti-premium" />
          <Kpi label={p('racconti_free')} value={racconti.length - premiumN} testId="panel-racconti-free" />
          {creatorSum && (
            <>
              <Kpi label={p('saves_total')} value={creatorSum.racconti_saves_total} testId="panel-saves-total" />
              <Kpi label={p('saves_unique')} value={creatorSum.racconti_saves_unique_users} testId="panel-saves-unique" />
            </>
          )}
        </PanelCard>
      )}

      {isVip && (
        <PanelCard title={p('calls_title')} icon={Phone} testId="panel-calls" actionLabel={p('open_calls')} onAction={openCalls} actionTestId="panel-open-calls">
          <Kpi label={p('calls_completed')} value={completedCalls.length} testId="panel-calls-completed" />
          <Kpi label={p('calls_missed')} value={missedCalls.length} testId="panel-calls-missed" />
          <Kpi label={p('calls_minutes')} value={callMinutes} testId="panel-calls-minutes" />
          <Kpi label={p('calls_earnings')} value={fmtMoney(callEarn)} testId="panel-calls-earnings" />
          <Kpi label={p('calls_avg')} value={avgCall} testId="panel-calls-avg" />
        </PanelCard>
      )}
    </div>
  );
};

// HOTFIX UI — exported for reuse by ScheduledLivesPanel (Crea → Programma
// una Live): same list, same actions (start/edit/duplicate/cancel/delete),
// zero duplicated logic.
export const FutureTab = ({ items, onRefresh, onEdit, t }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [busy, setBusy] = useState(null);

  const handleDelete = async (id, hasBookings) => {
    if (hasBookings) {
      toast({
        title: t('dashboard.future.cannot_delete_title'),
        description: t('dashboard.future.cannot_delete_desc'),
        variant: 'destructive',
      });
      return;
    }
    if (!window.confirm(t('dashboard.future.delete_confirm'))) return;
    setBusy(id);
    try {
      await deleteLive(id);
      toast({ title: t('dashboard.future.delete_success') });
      onRefresh();
    } catch (e) {
      toast({ title: t('dashboard.future.delete_error'), description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm(t('dashboard.future.cancel_confirm'))) return;
    setBusy(id);
    try {
      await cancelLive(id);
      toast({ title: t('dashboard.future.cancel_success') });
      onRefresh();
    } catch (e) {
      toast({ title: t('dashboard.future.cancel_error'), description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const handleDuplicate = async (id) => {
    setBusy(id);
    try {
      const dup = await duplicateLive(id);
      toast({ title: t('dashboard.future.duplicate_success_title'), description: t('dashboard.future.duplicate_success_desc') });
      onRefresh();
      navigate(`/lives/${dup.id}`);
    } catch (e) {
      toast({ title: t('dashboard.future.duplicate_error'), description: String(e.response?.data?.detail || '').split('|').pop() || '', variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const handleStart = async (id, kind) => {
    setBusy(id);
    try {
      const { updateLive } = await import('../api/lives');
      await updateLive(id, { kind });
      await startLive(id);
      toast({ title: t('schedule.starting') });
      navigate(`/lives/${id}`);
    } catch (e) {
      toast({ title: t('dashboard.future.could_not_go_live'), description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally { setBusy(null); }
  };

  if (!items.length) {
    return <Empty icon={Radio} text={t('dashboard.future.empty')} testId="future-empty" />;
  }

  return (
    <ul className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2" data-testid="future-list">
      {items.map((l) => (
        <li
          key={l.id}
          className="flex min-w-0 max-w-full flex-col gap-3 rounded-xl border border-border bg-card p-4"
          data-testid={`future-card-${l.id}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{l.title}</p>
              <p className="line-clamp-2 text-xs text-muted-foreground">{l.description || '—'}</p>
            </div>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
              l.status === 'live' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/15 gold-text'
            }`}>
              {t(`schedule.status_${l.status}`, { defaultValue: l.status })}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(l.scheduled_at)}</span>
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {l.duration_minutes} min</span>
            <span className="flex items-center gap-1">
              {l.is_paid ? <Crown className="h-3 w-3 gold-text" /> : <Radio className="h-3 w-3" />}
              {l.is_paid ? t('dashboard.future.paid_label', { amount: fmtMoney(l.price) }) : t('dashboard.future.free_label')}
            </span>
            <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {t('dashboard.future.participants', { count: l.participants_count })}</span>
          </div>
          {l.status === 'scheduled' && (
            <div className="flex flex-wrap gap-2 pt-1">
              <LiveScheduleCountdown scheduledAt={l.scheduled_at} isOwner testId={`future-countdown-${l.id}`} className="w-full text-xs gold-text" />
              <Button
                size="sm"
                onClick={() => handleStart(l.id, 'audio')}
                disabled={busy === l.id}
                className="h-9 gold-gradient text-xs text-black"
                data-testid={`future-start-audio-${l.id}`}
              >
                <Mic className="mr-1 h-3 w-3" /> {t('schedule.start')}
              </Button>
            </div>
          )}
          {['waiting_tracks', 'live'].includes(l.status) && (
            <Button onClick={() => navigate(`/lives/${l.id}`)} className="gold-gradient text-black" data-testid={`future-resume-${l.id}`}>
              {t('schedule.open')}
            </Button>
          )}
          <div className="flex flex-wrap items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit?.(l)}
              disabled={l.status !== 'scheduled'}
              className="h-8 text-xs"
              data-testid={`future-manage-${l.id}`}
              title={t('dashboard.future.manage_title')}
            >
              <Pencil className="mr-1 h-3 w-3" /> {t('dashboard.future.manage')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDuplicate(l.id)}
              disabled={busy === l.id}
              className="h-8 text-xs"
              data-testid={`future-duplicate-${l.id}`}
            >
              <Copy className="mr-1 h-3 w-3" /> {t('dashboard.future.duplicate')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleCancel(l.id)}
              disabled={busy === l.id || l.status !== 'scheduled'}
              className="h-8 text-xs text-amber-400 hover:bg-amber-500/10"
              data-testid={`future-cancel-${l.id}`}
            >
              <Ban className="mr-1 h-3 w-3" /> {t('dashboard.future.cancel_btn')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(l.id, l.has_bookings)}
              disabled={busy === l.id || l.has_bookings || l.status !== 'scheduled'}
              className="ml-auto h-8 text-xs text-red-400 hover:bg-red-500/10 disabled:opacity-40"
              data-testid={`future-delete-${l.id}`}
              title={l.has_bookings ? t('dashboard.future.delete_disabled_title') : t('dashboard.future.delete_btn')}
            >
              <Trash2 className="mr-1 h-3 w-3" /> {t('dashboard.future.delete_btn')}
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
};

const HistoryTab = ({ items, isVip, t }) => {
  if (!items.length) {
    return <Empty icon={History} text={t('dashboard.history.empty')} testId="history-empty" />;
  }
  const realMinutes = (l) => {
    if (l.started_at && l.ended_at) {
      return Math.max(1, Math.round((new Date(l.ended_at) - new Date(l.started_at)) / 60000));
    }
    return l.duration_minutes;
  };
  const h = (k) => t(`dashboard.history_row.${k}`);
  const nd = t('dashboard.panel.not_tracked');
  return (
    <ul className="divide-y divide-border rounded-xl border border-border bg-card" data-testid="history-list">
      {items.map((l) => (
        <li key={l.id} className="px-4 py-3" data-testid={`history-row-${l.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{l.title}</p>
              <p className="text-[11px] text-muted-foreground">
                {fmtDate(l.ended_at || l.scheduled_at)} · {realMinutes(l)} min
                {l.category ? ` · ${l.category}` : ''}
                {' · '}{l.is_paid ? h('paid') : h('free')}
              </p>
            </div>
            {isVip && (
              <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-semibold gold-text tabular-nums" data-testid={`history-earnings-${l.id}`}>
                {fmtMoney(l.earnings_net)}
              </span>
            )}
          </div>
          {/* Pannello — Prenotati · Partecipanti unici · Picco (n/d = live
              precedente al tracking, mai un falso zero) */}
          <div className="mt-1.5 flex flex-wrap gap-1.5 text-[10px]" data-testid={`history-metrics-${l.id}`}>
            <span className="rounded-full bg-secondary px-2 py-0.5 font-semibold text-muted-foreground">
              {h('bookings')}: <span className="text-foreground tabular-nums">{l.bookings_count}</span>
            </span>
            <span className="rounded-full bg-secondary px-2 py-0.5 font-semibold text-muted-foreground" data-testid={`history-unique-${l.id}`}>
              {h('unique')}: <span className="text-foreground tabular-nums">{l.unique_participants ?? nd}</span>
            </span>
            <span className="rounded-full bg-secondary px-2 py-0.5 font-semibold text-muted-foreground" data-testid={`history-peak-${l.id}`}>
              {h('peak')}: <span className="text-foreground tabular-nums">{l.peak_concurrent ?? nd}</span>
            </span>
            {isVip && l.tickets_sold != null && (
              <span className="rounded-full bg-secondary px-2 py-0.5 font-semibold text-muted-foreground">
                {h('tickets')}: <span className="text-foreground tabular-nums">{l.tickets_sold}</span>
              </span>
            )}
            {isVip && l.tips_credits != null && (
              <span className="rounded-full bg-secondary px-2 py-0.5 font-semibold text-muted-foreground">
                {h('tips')}: <span className="text-foreground tabular-nums">{fmtMoney(l.tips_credits)}</span>
              </span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
};

const OneToOneTab = ({ t }) => {
  // Phase 54 — instant pay-per-minute calls: state toggle + rate picker +
  // call history. The legacy availability/slot builder is archived.
  return (
    <div className="space-y-6" data-testid="one-to-one-tab">
      <CreatorCallSettings />
      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Storico chiamate
        </h3>
        <CreatorCallHistory />
      </div>
    </div>
  );
};

const CalendarTab = ({ items, t }) => {
  const grouped = useMemo(() => {
    const m = new Map();
    items.forEach((e) => {
      const dt = new Date(e.scheduled_at);
      const day = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
      if (!m.has(day)) m.set(day, []);
      m.get(day).push(e);
    });
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);

  if (!items.length) {
    return <Empty icon={Calendar} text={t('dashboard.calendar.empty')} testId="calendar-empty" />;
  }
  return (
    <ul className="space-y-3" data-testid="calendar-list">
      {grouped.map(([day, entries]) => (
        <li key={day} className="rounded-xl border border-border bg-card p-4" data-testid={`calendar-day-${day}`}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {new Date(`${day}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long', day: '2-digit', month: 'long' })}
          </p>
          <ul className="space-y-1">
            {entries.map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="flex items-center gap-2">
                  <span className={`inline-block h-2 w-2 rounded-full ${e.kind === 'live' ? 'bg-red-500' : 'gold-gradient'}`} />
                  <span className="text-foreground">{e.title}</span>
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {new Date(e.scheduled_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} ·
                  {' '}{e.duration_minutes} min · <span className="gold-text">{t('dashboard.calendar.busy')}</span>
                </span>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
};

// F9.6 — Movimenti/estratto conto: WalletLedger + rimborsi ricollocati
// dal Wallet. Stessi componenti, stessi endpoint.
const MovementsTab = ({ mode }) => (
  <div className="space-y-2" data-testid="movements-tab">
    <WalletLedger mode={mode} />
    <RefundRequestCard />
  </div>
);

const EuroKpi = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
    <div className={`mb-2 flex h-8 w-8 items-center justify-center rounded-lg ${accent}`}>
      <Icon className="h-4 w-4" />
    </div>
    <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">{value}</p>
    {sub && <p className="mt-0.5 text-[11px] text-muted-foreground">{sub}</p>}
  </div>
);

// F9.6 — strumenti professionali Voce Pro ricollocati dal Wallet:
// KPI economici, soglia/richiesta payout, verifica prelievi (una tantum)
// e EarningsDashboard. Logica e endpoint INVARIATI.
const EarningsTab = ({ summary, user, tw, onOpenPayout }) => {
  const available = summary?.available_balance ?? 0;
  return (
    <div className="space-y-5" data-testid="earnings-tab">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <EuroKpi
          icon={Wallet}
          label={tw('kpi.available')}
          value={fmtMoney(available)}
          sub={`≈ €${(summary?.available_balance_eur ?? 0).toFixed(2)}`}
          accent="bg-emerald-500/20 text-emerald-300"
          testId="wallet-kpi-available"
        />
        <EuroKpi
          icon={Hourglass}
          label={tw('kpi.maturing')}
          value={fmtMoney(summary?.maturing_balance ?? 0)}
          sub={`≈ €${(summary?.maturing_balance_eur ?? 0).toFixed(2)}`}
          accent="bg-amber-500/20 gold-text"
          testId="wallet-kpi-maturing"
        />
        <EuroKpi
          icon={TrendingUp}
          label={tw('kpi.total_earned')}
          value={fmtMoney(summary?.total_earned ?? 0)}
          sub={`≈ €${(summary?.total_earned_eur ?? 0).toFixed(2)}`}
          accent="bg-blue-500/20 text-blue-300"
          testId="wallet-kpi-earned"
        />
        <EuroKpi
          icon={Banknote}
          label={tw('kpi.total_payouts')}
          value={fmtMoney(summary?.total_payouts ?? 0)}
          sub={`≈ €${(summary?.total_payouts_eur ?? 0).toFixed(2)}`}
          accent="bg-violet-500/20 text-violet-300"
          testId="wallet-kpi-payouts"
        />
      </div>

      {available >= 200 ? (
        <div
          className="flex items-center gap-3 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-4"
          data-testid="payout-threshold-ready"
        >
          <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-300" />
          <div>
            <p className="text-sm font-semibold text-emerald-200">{tw('payout_threshold.ready')}</p>
            <p className="text-xs text-muted-foreground">{tw('payout_threshold.ready_detail')}</p>
          </div>
        </div>
      ) : (
        <div
          className="flex items-center gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4"
          data-testid="payout-threshold-below"
        >
          <Hourglass className="h-5 w-5 shrink-0 gold-text" />
          <div>
            <p className="text-sm font-semibold gold-text">
              {tw('payout_threshold.below', { eur: creditsToEur(200 - available).toFixed(2) })}
            </p>
            <p className="text-xs text-muted-foreground">
              {tw('payout_threshold.below_detail', { credits: fmtMoney(available) })}
            </p>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          onClick={onOpenPayout}
          disabled={available <= 0}
          className="h-10 gold-gradient px-5 text-sm font-semibold disabled:opacity-50"
          data-testid="open-payout-btn"
        >
          <ArrowDownToLine className="mr-2 h-4 w-4" /> {tw('page.request_payout')}
        </Button>
      </div>

      {/* Verifica prelievi una tantum (Stripe Connect) — NON è un secondo
          onboarding Voce Pro: appare solo finché i prelievi non sono attivi. */}
      {!user?.is_payout_ready && (
        <div id="wallet-payout-setup" data-testid="wallet-payout-setup">
          <div className="mb-2 flex items-center gap-2">
            <Banknote className="h-4 w-4 gold-text" />
            <h2 className="text-sm font-semibold uppercase tracking-wide gold-text">{tw('payout_setup.title')}</h2>
          </div>
          <p className="mb-3 text-xs text-muted-foreground">{tw('payout_setup.desc')}</p>
          <PayoutOnboardingCard />
        </div>
      )}

      <EarningsDashboard />
    </div>
  );
};

const StatsTab = ({ data, t }) => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="stats-grid">
    <StatTile icon={Radio}    label={t('dashboard.stats.total_lives_created')}    value={data.total_lives_created}    testId="stat-total-created" />
    <StatTile icon={History}  label={t('dashboard.stats.total_lives_completed')}  value={data.total_lives_completed}  testId="stat-total-completed" />
    <StatTile icon={Users}    label={t('dashboard.stats.total_participants')}     value={data.total_participants}     testId="stat-total-participants" />
    <StatTile icon={Phone}    label={t('dashboard.stats.total_1to1')}             value={data.total_one_to_one}       testId="stat-total-1to1" />
    <StatTile icon={Euro}     label={t('dashboard.stats.revenue_total')}          value={fmtMoney(data.revenue_total)} testId="stat-revenue-total" accent />
    <StatTile icon={Euro}     label={t('dashboard.stats.revenue_this_month')}     value={fmtMoney(data.revenue_this_month)} testId="stat-revenue-month" />
    <StatTile icon={Euro}     label={t('dashboard.stats.platform_fees')}          value={fmtMoney(data.platform_fees)} testId="stat-fees" />
    <StatTile icon={Euro}     label={t('dashboard.stats.pending_balance')}        value={fmtMoney(data.pending_balance)} testId="stat-pending" />
    <StatTile icon={Euro}     label={t('dashboard.stats.paid_out')}               value={fmtMoney(data.paid_out_total)} testId="stat-paid-out" />
  </div>
);

const LiveDashboardPage = () => {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('lives');
  const { t: tw } = useTranslation('wallet');
  // F9.6 — unica fonte di verità dei ruoli, condivisa col Wallet.
  const { isCreatorReady, isInOnboarding, isBuyerOnly, showCreatorSurface } = useWalletRole();
  const isVip = isCreatorReady;
  const writable = canWrite(user);
  const { toast } = useToast();
  // Phase F3 — Voice-First: hide Live Video actions when the flag is OFF.
  const { videoLiveEnabled } = useAppConfig();

  const [tab, setTab] = useState('overview');
  const [createOpen, setCreateOpen] = useState(false);
  // Phase F8.1 — deep-link ?tab=one-to-one from /create (Chiamate card):
  // applies the EXACT same gating as the one_to_one action card below.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const wanted = searchParams.get('tab');
    if (!wanted) return;
    if (wanted === 'one-to-one') {
      if (!isVip) {
        setO2oGateOpen(true);
        return;
      }
      setTab('one-to-one');
      return;
    }
    // F9.6 — deep-link alle sezioni ricollocate dal Wallet.
    if (['overview', 'movements', 'future', 'history', 'calendar'].includes(wanted)) setTab(wanted);
    if (wanted === 'earnings' && showCreatorSurface) setTab('earnings');
  }, [searchParams, isVip, showCreatorSurface]);
  // Phase 45 · Feb 2026 — dialog shown when a non-VIP user clicks the
  // One-to-One action card. VIP creators bypass the dialog and jump
  // directly to the one_to_one tab (unchanged behaviour).
  const [o2oGateOpen, setO2oGateOpen] = useState(false);
  // Phase 55 — pre-flight dialog for Live Now (title/category/cover required).
  const [goLiveOpen, setGoLiveOpen] = useState(false);
  const [goLiveKind, setGoLiveKind] = useState('video');
  const [liveNowFormat, setLiveNowFormat] = useState(() => {
    try {
      const saved = localStorage.getItem('ig_live_now_format');
      if (saved === '16:9' || saved === '1:1' || saved === '9:16') return saved;
    } catch (_e) { /* noop */ }
    return '16:9';
  });
  const persistLiveNowFormat = (v) => {
    setLiveNowFormat(v);
    try { localStorage.setItem('ig_live_now_format', v); } catch (_e) { /* noop */ }
  };
  const [editLive, setEditLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [future, setFuture] = useState([]);
  const [history, setHistory] = useState([]);
  const [oneToOne, setOneToOne] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [stats, setStats] = useState(null);
  // Pannello di controllo — extra read-only data.
  const [wallet, setWallet] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [creatorSum, setCreatorSum] = useState(null);
  const [myPosts, setMyPosts] = useState([]);
  const [calls, setCalls] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const endpoints = [
      { key: 'overview',  fn: dashboardOverview,  set: setOverview  },
      { key: 'future',    fn: dashboardFuture,    set: setFuture    },
      { key: 'history',   fn: dashboardHistory,   set: setHistory   },
      { key: 'one-to-one', fn: dashboardOneToOne, set: setOneToOne  },
      { key: 'calendar',  fn: dashboardCalendar,  set: setCalendar  },
      { key: 'wallet',    fn: async () => (await api.get('/wallet/summary')).data, set: setWallet },
      { key: 'blocked',   fn: async () => (await api.get('/users/me/blocked')).data || [], set: setBlocked },
    ];
    try {
      const results = await Promise.allSettled(endpoints.map((e) => e.fn()));
      const failed = [];
      results.forEach((res, idx) => {
        const ep = endpoints[idx];
        if (res.status === 'fulfilled') {
          ep.set(res.value);
        } else {
          const e = res.reason;
          const detail = e?.response?.data?.detail || e?.message || 'Network error';
          failed.push(`${ep.key}: ${detail}`);
        }
      });
      if (failed.length > 0) {
        setLoadError(failed.join(' · '));
        toast({
          title: failed.length === endpoints.length
            ? t('dashboard.load_error_all')
            : t('dashboard.load_error_partial', { failed: failed.length, total: endpoints.length }),
          description: failed.join(' · ') + (
            failed.some((f) => f.toLowerCase().includes('network'))
              ? t('dashboard.network_hint')
              : ''
          ),
          variant: 'destructive',
        });
      }
      if (isVip) {
        try {
          const s = await dashboardStats();
          setStats(s);
        } catch (_e) { /* silently skip stats */ }
        // Pannello — VIP extras, each independent and non-blocking.
        try { setCreatorSum(await creatorSummary()); } catch (_e) { /* skip */ }
        try { setMyPosts(await fetchUserPosts(user.username)); } catch (_e) { /* skip */ }
        try {
          const r = await api.get('/o2o/calls-history', { params: { role: 'creator', limit: 100 } });
          setCalls(Array.isArray(r.data) ? r.data : []);
        } catch (_e) { /* skip */ }
      }
    } finally {
      setOverview((prev) => prev || {
        future_count: 0,
        total_bookings: 0,
        one_to_one_requests: 0,
        completed_count: 0,
        is_vip: isVip,
        earnings_total: 0,
      });
      setLoading(false);
    }
  }, [isVip, toast, t, user?.username]);

  useEffect(() => { load(); }, [load]);

  // F9.6 — payout ricollocato dal WalletPage (stessa logica, stesso endpoint).
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutBusy, setPayoutBusy] = useState(false);

  const openPayout = () => {
    if (!user?.is_payout_ready) {
      toast({ title: tw('payout_setup.toast_title'), description: tw('payout_setup.toast_desc') });
      setTimeout(() => {
        document.getElementById('wallet-payout-setup')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
      return;
    }
    setPayoutAmount(wallet ? String(wallet.available_balance ?? 0) : '');
    setPayoutOpen(true);
  };

  const submitPayout = async () => {
    const amt = parseFloat(payoutAmount);
    if (isNaN(amt) || amt <= 0) {
      toast({ title: tw('payout_dialog.invalid_amount'), variant: 'destructive' });
      return;
    }
    // Phase 91 — minimum payout threshold (Terms sez. 10): 200 Credits = €20.00.
    if (amt < 200) {
      toast({ title: tw('payout_dialog.min_note'), variant: 'destructive' });
      return;
    }
    setPayoutBusy(true);
    try {
      await requestPayout(amt);
      toast({ title: tw('payout_dialog.success_title'), description: tw('payout_dialog.success_desc') });
      setPayoutOpen(false);
      await load();
      await refresh();
    } catch (err) {
      const detail = String(err.response?.data?.detail || '');
      if (detail.startsWith('stripe_onboarding_required')) {
        setPayoutOpen(false);
        toast({ title: tw('payout_setup.required_title'), description: tw('payout_setup.required_desc') });
        setTimeout(() => {
          document.getElementById('wallet-payout-setup')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 250);
      } else {
        toast({
          title: tw('payout_dialog.error_title'),
          description: detail.includes('|') ? detail.split('|')[1] : (detail || tw('payout_dialog.error_default')),
          variant: 'destructive',
        });
      }
    } finally {
      setPayoutBusy(false);
    }
  };

  const tabs = useMemo(() => {
    const list = [
      { id: 'overview',     label: t('dashboard.tabs.overview'),   icon: LayoutGrid },
      { id: 'movements',    label: t('dashboard.tabs.movements'),  icon: ReceiptText },
      { id: 'future',       label: t('dashboard.tabs.future'),     icon: Radio },
      { id: 'history',      label: t('dashboard.tabs.history'),    icon: History },
      { id: 'one-to-one',   label: t('dashboard.tabs.one_to_one'), icon: Phone },
      { id: 'calendar',     label: t('dashboard.tabs.calendar'),   icon: Calendar },
    ];
    if (showCreatorSurface) list.push({ id: 'earnings', label: t('dashboard.tabs.earnings'), icon: TrendingUp });
    if (isVip) list.push({ id: 'stats', label: t('dashboard.tabs.stats'), icon: BarChart3 });
    return list;
  }, [isVip, showCreatorSurface, t]);

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center" data-testid="dashboard-loading">
        <Loader2 className="h-7 w-7 animate-spin gold-text" />
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="mx-auto flex h-[60vh] max-w-md flex-col items-center justify-center gap-3 px-4 text-center" data-testid="dashboard-error">
        <p className="text-sm text-muted-foreground">{t('dashboard.unavailable')}</p>
        {loadError ? (
          <p className="text-xs text-muted-foreground/70 break-all">{loadError}</p>
        ) : null}
        <Button onClick={load} variant="secondary" className="h-9 text-sm" data-testid="dashboard-retry-btn">
          {t('dashboard.retry')}
        </Button>
      </div>
    );
  }

  return (
    <main className="mx-auto w-full max-w-5xl px-4 pb-24 pt-4 md:pt-8" data-testid="live-dashboard-page">
      <BackButton className="mb-3" />
      <header className="mb-4">
        <h1 className="flex items-center gap-2 text-2xl font-semibold gold-text" data-testid="dashboard-title">
          <LayoutDashboard className="h-6 w-6" /> {t('dashboard.title')}
        </h1>
        <p className="text-xs text-muted-foreground md:text-sm" data-testid="dashboard-subtitle">
          {t('dashboard.subtitle')}
        </p>
      </header>

      {/* Phase 45 · Feb 2026 — Hero card visibile solo ai non-VIP: presenta
          la Live Dashboard come punto di partenza per la monetizzazione e
          spinge verso l'Onboarding Creator. */}
      {!isVip && <EarnWithLivesHero />}

      {/* HOTFIX UI — le action card (Live Now / Programma / Chiamate) sono
          state spostate in `Crea`, unico punto operativo. Il Pannello resta
          focalizzato su monitoraggio e controllo. Nessuna funzione rimossa:
          tab, dialoghi e deep-link (?tab=one-to-one, ?tab=future) invariati. */}

      <ReadOnlyNotice action={t('dashboard.readonly_action')} className="mb-4" />

      {/* Card Persone — Voce Pro without an O2O rate have NO "Chiama" button:
          nudge them to set one (hidden on the One-to-One tab itself). */}
      {isVip && !(user?.o2o_rate_credits_per_min > 0) && tab !== 'one-to-one' && (
        <div
          className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3"
          data-testid="o2o-rate-reminder"
        >
          <Phone className="h-5 w-5 shrink-0 gold-text" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold gold-text">{t('dashboard.o2o_rate_reminder_title')}</p>
            <p className="text-xs text-muted-foreground">{t('dashboard.o2o_rate_reminder_desc')}</p>
          </div>
          <Button
            size="sm"
            className="gold-gradient h-8 rounded-full text-xs font-bold text-black hover:opacity-90"
            onClick={() => setTab('one-to-one')}
            data-testid="o2o-rate-reminder-cta"
          >
            {t('dashboard.o2o_rate_reminder_cta')}
          </Button>
        </div>
      )}

      <nav className="mb-5 flex gap-2 overflow-x-auto pb-1 no-scrollbar" data-testid="dashboard-tabs">
        {tabs.map((tt) => (
          <TabBtn
            key={tt.id}
            id={tt.id}
            label={tt.label}
            icon={tt.icon}
            active={tab === tt.id}
            onClick={() => { setTab(tt.id); setSearchParams({ tab: tt.id }, { replace: true }); }}
            testId={`dashboard-tab-${tt.id}`}
          />
        ))}
      </nav>

      <section>
        {tab === 'overview'   && (
          <>
            {isInOnboarding && <BuyerOnboardingCard />}
            <OverviewTab
              data={overview}
              isVip={isVip}
              t={t}
              wallet={wallet}
              blocked={blocked}
              creatorSum={creatorSum}
              posts={myPosts}
              calls={calls}
              stats={stats}
              navigate={navigate}
              openCalendar={() => setTab('calendar')}
              openCalls={() => setTab('one-to-one')}
            />
          </>
        )}
        {tab === 'movements'  && (
          <MovementsTab mode={isBuyerOnly || isInOnboarding ? 'buyer' : 'creator'} />
        )}
        {tab === 'future'     && <FutureTab items={future} onRefresh={load} t={t} onEdit={async (l) => {
          try {
            const full = await getLive(l.id);
            setEditLive(full);
          } catch (_e) {
            setEditLive(l);
          }
        }} />}
        {tab === 'history'    && <HistoryTab items={history} isVip={isVip} t={t} />}
        {tab === 'one-to-one' && <OneToOneTab items={oneToOne} t={t} />}
        {tab === 'calendar'   && <CalendarTab items={calendar} t={t} />}
        {tab === 'earnings' && showCreatorSurface && (
          <EarningsTab summary={wallet} user={user} tw={tw} onOpenPayout={openPayout} />
        )}
        {tab === 'stats' && isVip && stats && <StatsTab data={stats} t={t} />}
      </section>

      <CreateLiveDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={load}
      />
      <CreateLiveDialog
        open={!!editLive}
        onOpenChange={(o) => { if (!o) setEditLive(null); }}
        editLive={editLive}
        onUpdated={() => { setEditLive(null); load(); }}
      />
      <OneToOneVipRequiredDialog open={o2oGateOpen} onOpenChange={setO2oGateOpen} />
      {/* Phase 55 — Live Now pre-flight (titolo/categoria obbligatori) */}
      <GoLiveNowDialog
        open={goLiveOpen}
        onOpenChange={setGoLiveOpen}
        kind={goLiveKind}
        initialFormat={liveNowFormat}
      />
      {/* F9.6 — dialog payout ricollocato dal WalletPage (comportamento identico) */}
      <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{tw('payout_dialog.title')}</DialogTitle>
            <DialogDescription>
              {tw('payout_dialog.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-2">
            <p className="text-sm text-muted-foreground">
              {tw('payout_dialog.balance')}{' '}
              <span className="font-semibold">{fmtMoney(wallet?.available_balance ?? 0)}</span>
              <span className="ml-1 text-xs text-muted-foreground">
                (≈ €{creditsToEur(wallet?.available_balance ?? 0).toFixed(2)})
              </span>
            </p>
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground">
                {tw('payout_dialog.amount_label')}
              </label>
              <Input
                type="number"
                step="1"
                min="200"
                max={wallet?.available_balance ?? 0}
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                data-testid="wallet-payout-amount"
              />
              <p className="mt-1 text-[11px] text-muted-foreground" data-testid="payout-min-note">
                {tw('payout_dialog.min_note')}
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {tw('payout_dialog.will_receive', { eur: creditsToEur(Number(payoutAmount || 0)).toFixed(2) })}
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              {user?.is_payout_ready
                ? tw('payout_dialog.hint_vip')
                : tw('payout_dialog.hint_manual')}
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPayoutOpen(false)}>
              {tw('payout_dialog.cancel')}
            </Button>
            <Button
              onClick={submitPayout}
              disabled={payoutBusy}
              className="bg-black hover:bg-neutral-800"
              data-testid="wallet-payout-submit"
            >
              {payoutBusy ? tw('payout_dialog.submitting') : tw('payout_dialog.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default LiveDashboardPage;
